import { FILE_ID_MATCHER, convertComplexConditionToComponentsGroupConditions } from "../../utils/rulesetHelpers"
import type { GroupConditions } from "../Ruleset"
import { isRulesetRule } from "../Ruleset"
import { isTableFormatFormikValues } from "../ScoreTables/helpers"
import { isScorecardRule } from "../Scorecards/helpers"
import type { ComponentTypeExtractor, Variant } from "./Interfaces"

/**
 * Constructs initial formik values for a component based on its fetched data.
 *
 * This function processes the provided component fetched data to extract the
 * active version and its rules. It then constructs formik-compatible initial
 * values for the component, which include details such as the component's name,
 * unique identifier, and a list of cards representing rules with their
 * conditions and associated metadata.
 *
 * @param componentFetchedData - An object containing data about a component,
 * including its versions and other relevant details.
 * @returns An object containing the initial formik values for the component,
 * or undefined if no active version with rules is found.
 */
export const constructComponentInitialFormikValues = <TVariant extends Variant>(
  componentFetchedData: ComponentTypeExtractor<TVariant, "componentData">,
): ComponentTypeExtractor<TVariant, "formikValues"> | undefined => {
  const activeVersion = componentFetchedData.active_version

  if (!activeVersion?.rules) return

  const cards = activeVersion.rules?.map((rule) => {
    // Determine component type based on the rule structure
    const isRuleset = isRulesetRule(rule)
    const isScorecard = isScorecardRule(rule)
    const componentType = isRuleset ? "Ruleset" : isScorecard ? "Scorecard" : "Taglist"
    const groupConditions = convertComplexConditionToComponentsGroupConditions(rule?.condition, componentType)

    return {
      ...groupConditions,
      name: rule?.name,
      condition_list_files: rule.condition_list_files,
      label: isRuleset ? rule.return_label.name : undefined,
      label_id: isRuleset ? rule.return_label.uuid : undefined,
      return_value: isScorecardRule(rule) ? rule.return_value : undefined,
    }
  })

  return {
    name: `${componentFetchedData.name} - Copy`,
    uuid: componentFetchedData.uuid,
    cards,
  }
}

/**
 * Type guard to determine if a given variant name is not "Scorecard".
 *
 * This function checks whether the provided variant name is not of type "Scorecard".
 * It serves as a type guard, ensuring that the variant name can be safely used
 * in contexts where "Scorecard" should be excluded.
 *
 * @param name - The variant name to check.
 * @returns A boolean indicating whether the name is not "Scorecard".
 */
export function isNotScorecardCompName<TVariant extends Variant>(
  name: TVariant,
): name is Exclude<TVariant, "Scorecard"> {
  return name !== "Scorecard"
}

// Legacy updater removed. Unified updater below handles all variants including score tables.

/**
 * Recursively updates file IDs in group conditions.
 * This helper function processes nested groups and their conditions to update file references.
 *
 * @param groups - Array of GroupConditions to update
 * @param filesIdsMap - Mapping of old file IDs to new file IDs
 * @returns Updated array of GroupConditions with new file IDs
 */
function updateGroupConditionsFileIds(
  groups: Array<GroupConditions>,
  filesIdsMap: Record<string, string>,
): Array<GroupConditions> {
  return groups.map((group) => {
    // Update conditions in this group
    if (Array.isArray(group.conditions) && group.conditions.length > 0) {
      const conditionWithFiles = group.conditions.filter((condition: { value: string }) =>
        condition.value.includes("$file"),
      )

      if (conditionWithFiles.length > 0) {
        conditionWithFiles.forEach((file: { value: string }) => {
          const fileId = file.value.match(FILE_ID_MATCHER)?.[1] ?? ""
          const clonedFileId = filesIdsMap[fileId]

          if (clonedFileId) {
            file.value = `$file<${clonedFileId}>`
          }
        })
      }
    }

    // Recursively update nested groups
    if (Array.isArray(group.groups) && group.groups.length > 0) {
      group.groups = updateGroupConditionsFileIds(group.groups, filesIdsMap)
    }

    return group
  })
}

/**
 * Updates file IDs in a ruleset component's conditions and file references.
 * This function specifically handles ruleset components and updates file references
 * in all condition levels and condition_list_files.
 *
 * @param component - The ruleset component with formik values
 * @param filesIdsMap - Mapping of old file IDs to new file IDs
 * @returns The updated ruleset component with new file IDs
 *
 * @example
 * const rulesetComponent = {
 *   name: "Age Validation",
 *   cards: [{
 *     conditions: [{ value: "$file<old-id-123>", ... }],
 *     groups: [{
 *       conditions: [{ value: "$file<old-id-123>", ... }],
 *       groups: [{
 *         conditions: [{ value: "$file<old-id-123>", ... }],
 *         groups: []
 *       }]
 *     }]
 *     condition_list_files: [{ uuid: "old-id-123", name: "data.csv", version: "1.0" }]
 *   }]
 * };
 * const filesMap = { "old-id-123": "new-id-456" };
 * const updated = updateRulesetDataFilesIds(rulesetComponent, filesMap);
 * // Returns component with updated file IDs: "$file<new-id-456>" and uuid: "new-id-456"
 */
export function updateComponentsDataFilesIds<TVariant extends Variant>(
  component: ComponentTypeExtractor<TVariant, "formikValues">,
  filesIdsMap: Record<string, string>,
): ComponentTypeExtractor<TVariant, "formikValues"> {
  // Handle Score Table format
  if (isTableFormatFormikValues(component)) {
    component.criteria_files = component.criteria_files.map((file) => {
      return {
        name: file.name,
        uuid: (filesIdsMap[file.uuid] as string) ?? file.uuid,
        version: file.version,
      }
    })

    component.criteria = component.criteria.map((criteria) =>
      criteria.map((item) => {
        const fileId = item.condition.match(FILE_ID_MATCHER)?.[1] ?? ""
        const clonedFileId = filesIdsMap[fileId]

        if (clonedFileId) {
          item.condition = item.condition.replace(fileId, clonedFileId)
          // Some table criteria may include file id in value too
          item.value = item.value?.replace(fileId, clonedFileId)
        }
        return item
      }),
    )

    return component
  }

  // Handle cards-based formats (Ruleset, Taglist, Scorecard)
  component.cards = component.cards.map((card) => {
    card.condition_list_files = card.condition_list_files.map((file) => {
      const clonedFileId = filesIdsMap[file.uuid]

      return {
        name: file.name,
        uuid: (clonedFileId as string) ?? file.uuid,
        version: file.version,
      }
    })

    // Update level one conditions with new file IDs
    if (Array.isArray(card.conditions) && card.conditions.length > 0) {
      const conditionWithFiles = card.conditions.filter((condition) => condition.value.includes("$file"))

      if (conditionWithFiles.length > 0) {
        conditionWithFiles.forEach((file) => {
          const fileId = file.value.match(FILE_ID_MATCHER)?.[1] ?? ""
          const clonedFileId = filesIdsMap[fileId]

          if (clonedFileId) file.value = `$file<${clonedFileId}>`
        })
      }
    }

    // Update nested groups conditions with new file IDs
    if (Array.isArray(card.groups) && card.groups.length > 0) {
      card.groups = updateGroupConditionsFileIds(card.groups, filesIdsMap)
    }

    return card
  }) as typeof component.cards

  return component
}
